import { FormattedMessage } from "react-intl";
import AssetCoverToast from "common/features/asset/AssetCoverToast.tsx";
import { CourseMaterial } from "./coursesLoader";
import BaseLinkOrButton from "common/core-ui/BaseLinkOrButton";
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { optedOutWithoutBook, optedOutWithBook } from "./printStatusHelper";

declare global {
  interface WindowEventMap {
    [PrintOptInEvent]: CustomEvent;
    [ViewProgramDetailsEvent]: CustomEvent;
  }
}

type ToastProps = Pick<CourseMaterial, "printStatus" | "vbid"> & {
  termId: string | number;
  eaProgram: string;
  programExcludesPrint: boolean;
  onClose?: () => void;
  courseMaterialId: string | number;
};

export interface ConfirmOptInEvent {
  courseMaterialId: string | number;
}

export interface ViewProgramDetailsEvent {
  termId: string | number;
}

export const PrintOptInEvent = "confirmPrintOptIn";
export const ViewProgramDetailsEvent = "viewProgramDetails";

export default function PrintStatusChangeToast({
  printStatus,
  vbid,
  courseMaterialId,
  onClose,
  termId,
  eaProgram,
  programExcludesPrint = true,
}: ToastProps) {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/my-courses") {
      onClose?.();
    }
  }, [location, onClose]);

  if (!optedOutWithoutBook(printStatus) && !optedOutWithBook(printStatus)) {
    return null;
  }

  const announceConfirmPrintOptIn = () => {
    const event = new CustomEvent<ConfirmOptInEvent>(PrintOptInEvent, {
      detail: { courseMaterialId },
    });
    window.dispatchEvent(event);
    onClose?.();
  };

  const announceViewProgramDetails = () => {
    const event = new CustomEvent<ViewProgramDetailsEvent>(
      ViewProgramDetailsEvent,
      {
        detail: { termId },
      }
    );
    window.dispatchEvent(event);
    onClose?.();
  };

  const getMessage = () => {
    if (optedOutWithoutBook(printStatus)) {
      return (
        <FormattedMessage
          defaultMessage={`To gain access, select "Get Print Book" and confirm your pickup details.`}
        />
      );
    } else if (optedOutWithBook(printStatus)) {
      return (
        <FormattedMessage
          defaultMessage={`To keep this print book, select “Keep Print Book.” If not, return it to your bookstore.`}
        />
      );
    }
  };

  const getLinkMessage = () => {
    if (optedOutWithoutBook(printStatus)) {
      return <FormattedMessage defaultMessage={"Get Print Book"} />;
    } else if (optedOutWithBook(printStatus)) {
      return <FormattedMessage defaultMessage={"Keep Print Book"} />;
    }
  };

  return (
    <AssetCoverToast
      vbid={vbid}
      onClose={onClose}
      title={
        <FormattedMessage defaultMessage="The status of your print book has changed." />
      }
    >
      {programExcludesPrint ? (
        <>
          <FormattedMessage
            defaultMessage={`The status of your print book has changed. To gain access, opt back into {eaProgram} or purchase on your own.`}
            values={{ eaProgram }}
          />
          <BaseLinkOrButton
            onClick={() => announceViewProgramDetails()}
            className="vst-link"
          >
            <FormattedMessage defaultMessage={"Program Options"} />
          </BaseLinkOrButton>
        </>
      ) : (
        <>
          {getMessage()}
          <BaseLinkOrButton
            onClick={() => announceConfirmPrintOptIn()}
            className="vst-link"
          >
            {getLinkMessage()}
          </BaseLinkOrButton>
        </>
      )}
    </AssetCoverToast>
  );
}
